import angular from "angular";
import $ from 'jquery';
import 'bootstrap';

angular
    .module('ui-sentinel.relay')
    .controller('RelayAdminListController', RelayAdminListController);

/////////////

RelayAdminListController.$inject = ['$rootScope', '$timeout', 'FeedbackService', 'ApiErrorService', 'RelayService'];

function RelayAdminListController($rootScope, $timeout, FeedbackService, ApiErrorService, RelayService) {
    // Typing pauses this long before a lookup fires. Long enough that a typed IMEI costs one or
    // two requests rather than one per keystroke, short enough to feel immediate.
    const searchDebounceMs = 300;
    const minSearchLength = 3;
    // The API refuses a larger batch outright, so the limit is checked before anything is sent.
    const maxBulkImeis = 500;
    const confirmModalSelector = '#relayConfirmModal';
    const genericErrorMessage = "Unexpected error occurred while loading relay settings";
    const genericSaveErrorMessage = "Unexpected error occurred while saving the relay setting";
    const permissionsErrorMessage = "Unable to load your relay permissions. Reload the page to try again.";

    // Fallbacks for the two refusals a user is most likely to hit. The API sends its own message
    // with each, and that is preferred; these only apply if a response arrives without a body, so
    // a mistyped IMEI never surfaces as "unexpected error".
    const notAssignedMessage = "That IMEI is not currently assigned to an account you manage. Check the IMEI, and that the device is still assigned.";
    const noOverrideMessage = "There is no relay override on that device to remove.";
    const notAnEditorMessage = "You do not have permission to change relay settings.";
    const onActiveShipmentMessage = "That device is on a shipment that is under way. Changing where it reports mid-shipment stops the shipment receiving data. Wait for the shipment to complete, or ask OnAsset to make the change.";
    const euDetail = "The device must already be registered in the EU environment, otherwise its reports will arrive somewhere unreadable.";

    const vm = {
        permissions: { canEdit: false, isEntitled: false },
        permissionsLoaded: false,

        list: null,
        filter: 'all',
        searchText: null,
        page: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 100,
        errorMessage: null,
        feedback: FeedbackService,

        load: load,
        applyFilter: applyFilter,
        searchTextChanged: searchTextChanged,
        goToPage: goToPage,

        setImei: null,
        // Both add forms default to relaying to EU. A device with no row here follows its account
        // default, and that default is US for every account not configured to relay - which covers
        // the accounts whose named editors use this page, so an entry added here is nearly always
        // an EU exception. Where an account already defaults to EU this pre-selects the choice that
        // changes nothing, and the confirmation step is what guards a careless save either way.
        setValue: 'yes',
        setErrorMessage: null,
        setSubmit: setSubmit,

        suggestions: null,
        searching: false,
        imeiChanged: imeiChanged,
        pickSuggestion: pickSuggestion,
        dismissSuggestions: dismissSuggestions,

        rowSet: rowSet,
        rowClear: rowClear,

        bulkText: null,
        bulkValue: 'yes',
        bulkResult: null,
        bulkErrorMessage: null,
        bulkSubmit: bulkSubmit,
        bulkReset: bulkReset,

        confirmation: null,
        confirmSubmit: confirmSubmit,
        confirmCancel: confirmCancel,
    };

    // Declared before the return so the initializers actually run. Left below it these become
    // undefined once transpiled to var, and ++undefined is NaN - which never equals itself, so
    // every search result would look stale and be discarded.
    let searchTimer = null;
    let searchToken = 0;
    let pendingAction = null;

    activate();
    return vm;

    function activate() {
        vm.feedback.clear();
        RelayService.permissions().then(
            function (permissions) {
                vm.permissions = permissions;
                vm.permissionsLoaded = true;
                if (permissions.isEntitled) {
                    load();
                }
            },
            function () {
                vm.errorMessage = permissionsErrorMessage;
            }
        );
    }

    function applyFilter() {
        vm.page = 1;
        load();
    }

    // The clear button in a type=search box fires a change but never an Enter, so emptying the box
    // re-applies the filter immediately. Typing still waits for Enter or the Search button, which is
    // how the rest of this app behaves.
    function searchTextChanged() {
        if (!vm.searchText) {
            applyFilter();
        }
    }

    function goToPage(page) {
        vm.page = page;
        load();
    }

    // Prefers the API's own explanation, falling back to wording specific to the status so a
    // refusal is never reported as an unexpected failure.
    function describeError(error, fallback, notFoundFallback) {
        const status = error ? error.status : null;
        if (status === 404) {
            return ApiErrorService.getMessage(error, notFoundFallback || notAssignedMessage);
        }
        if (status === 403) {
            return ApiErrorService.getMessage(error, notAnEditorMessage);
        }
        // The API names the shipments in the way, which is the useful part; this only covers a
        // refusal that arrives without a body, so it never reads as an unexpected failure.
        if (status === 409) {
            return ApiErrorService.getMessage(error, onActiveShipmentMessage);
        }
        return ApiErrorService.getMessage(error, fallback);
    }

    function load() {
        $rootScope.loading = true;
        vm.errorMessage = null;

        const countPromise = RelayService.countOverrides(vm.filter, vm.searchText, vm.itemsPerPage).$promise;
        countPromise.then(
            function (result) {
                vm.totalPages = result.pageCount;
                vm.totalItems = result.itemCount;
            },
            function (error) {
                vm.errorMessage = ApiErrorService.getMessage(error, genericErrorMessage);
            }
        );

        const listPromise = RelayService.listOverrides(vm.filter, vm.searchText, vm.page, vm.itemsPerPage).$promise;
        listPromise.then(
            function (result) {
                vm.list = result;
            },
            function (error) {
                vm.errorMessage = ApiErrorService.getMessage(error, genericErrorMessage);
            }
        ).finally(function () {
            $rootScope.loading = false;
        });
    }

    // --- IMEI lookup -------------------------------------------------------------------------
    //
    // The device being set usually has no override row yet, so it cannot be picked from the table
    // below. This looks it up instead. The endpoint applies the same ownership rule the write does,
    // so anything offered here can actually be set.

    // searchTimer and searchToken are declared above the return, where their initializers run.
    // searchToken is incremented per search so a slow earlier response cannot overwrite a newer one.

    function imeiChanged() {
        vm.setErrorMessage = null;

        if (searchTimer) {
            $timeout.cancel(searchTimer);
            searchTimer = null;
        }

        const term = (vm.setImei || '').trim();
        if (term.length < minSearchLength) {
            vm.suggestions = null;
            vm.searching = false;
            return;
        }

        vm.searching = true;
        searchTimer = $timeout(function () {
            const token = ++searchToken;
            RelayService.searchSentries(term).$promise.then(
                function (results) {
                    if (token !== searchToken) {
                        return;
                    }
                    // An exact single match is what the user was typing, not a choice to make.
                    vm.suggestions = results.length === 1 && results[0].imei === term ? null : results;
                    vm.searching = false;
                },
                function () {
                    if (token !== searchToken) {
                        return;
                    }
                    // A failed lookup is not worth an error banner; the user can still type the
                    // IMEI in full and save, and the write reports its own refusal.
                    vm.suggestions = null;
                    vm.searching = false;
                }
            );
        }, searchDebounceMs);
    }

    function pickSuggestion(suggestion) {
        vm.setImei = suggestion.imei;
        vm.suggestions = null;
        vm.searching = false;
    }

    function dismissSuggestions() {
        vm.suggestions = null;
    }

    // Setting a device that has no row yet is the common case, so this takes a typed IMEI rather
    // than only offering actions on rows that already exist.
    function setSubmit() {
        const imei = (vm.setImei || '').trim();
        if (!imei) {
            vm.setErrorMessage = "Enter an IMEI";
            return;
        }

        vm.setErrorMessage = null;
        const relayEnabled = vm.setValue === 'yes';
        confirmRouting(imei, relayEnabled, function () {
            save(imei, relayEnabled, function () {
                vm.setImei = null;
                vm.suggestions = null;
            }, function (error) {
                vm.setErrorMessage = describeError(error, genericSaveErrorMessage);
            });
        });
    }

    function rowSet(row, relayEnabled) {
        confirmRouting(row.imei, relayEnabled, function () {
            save(row.imei, relayEnabled, null, function (error) {
                vm.errorMessage = `${row.imei}: ${describeError(error, genericSaveErrorMessage)}`;
            });
        });
    }

    function rowClear(row) {
        askToConfirm({
            title: 'Remove override',
            verb: 'remove the relay override on',
            subject: row.imei,
            suffix: '',
            detail: 'It will then follow its account default.'
        }, function () {
            clear(row);
        });
    }

    function clear(row) {
        $rootScope.loading = true;
        vm.errorMessage = null;
        vm.bulkResult = null;
        RelayService.clearOverride(row.imei).$promise.then(
            function () {
                vm.feedback.addSuccess(`Relay override removed for ${row.imei}; it now follows its account default.`);
                load();
            },
            function (error) {
                vm.errorMessage = `${row.imei}: ${describeError(error, genericSaveErrorMessage, noOverrideMessage)}`;
            }
        ).finally(function () {
            $rootScope.loading = false;
        });
    }

    function save(imei, relayEnabled, onSuccess, onError) {
        $rootScope.loading = true;
        // Only ever show the outcome of the most recent action; a leftover bulk result alongside a
        // single-device message reads as two unrelated things having just happened.
        vm.bulkResult = null;
        RelayService.setOverride(imei, relayEnabled).$promise.then(
            function () {
                vm.feedback.addSuccess(`Relay ${relayEnabled ? 'enabled' : 'disabled'} for ${imei}.`);
                if (onSuccess) {
                    onSuccess();
                }
                load();
            },
            onError
        ).finally(function () {
            $rootScope.loading = false;
        });
    }

    function bulkSubmit() {
        const imeis = Array.from(new Set((vm.bulkText || '')
            .split(/[\s,;]+/)
            .filter(function (i) { return i.length > 0; })));

        if (imeis.length === 0) {
            vm.bulkErrorMessage = "Enter one or more IMEIs";
            return;
        }

        if (imeis.length > maxBulkImeis) {
            vm.bulkErrorMessage = `Enter at most ${maxBulkImeis} IMEIs at a time; ${imeis.length} were entered.`;
            return;
        }

        vm.bulkErrorMessage = null;
        const enabling = vm.bulkValue === 'yes';
        askToConfirm({
            title: 'Change several devices',
            verb: enabling ? 'relay' : 'process',
            subject: `${imeis.length} device(s)`,
            suffix: enabling ? ' to the EU' : ' in the US',
            detail: enabling ? euDetail : null
        }, function () {
            bulkSave(imeis, enabling);
        });
    }

    function bulkSave(imeis, enabling) {
        $rootScope.loading = true;
        vm.feedback.clear();
        vm.bulkErrorMessage = null;
        vm.bulkResult = null;

        RelayService.setOverridesBulk(imeis, enabling).$promise.then(
            function (result) {
                // Partial success is normal here: the server applies what it can and reports the
                // rest. The headline goes to the feedback banner, the same place a single-device
                // change reports to, and the per-device failures stay in the card where there is
                // room to list them.
                vm.bulkResult = result;

                const updated = result.updated ? result.updated.length : 0;
                const unchanged = result.unchanged ? result.unchanged.length : 0;
                const failed = result.failed ? result.failed.length : 0;

                // Clear the list once nothing is left to act on, which is what the single-device
                // box already does. When something failed the text stays, because the failures
                // name the devices and correcting them in place is the next thing anyone does.
                if (failed === 0) {
                    vm.bulkText = null;
                }

                // Devices that already carried the requested value are reported separately, so
                // re-running a list reads as "one changed, two already set" instead of implying
                // three changes were made.
                const parts = [];
                if (updated > 0) {
                    parts.push(`Relay ${enabling ? 'enabled' : 'disabled'} for ${updated} device(s).`);
                }
                if (unchanged > 0) {
                    parts.push(`${unchanged} already set.`);
                }
                if (failed > 0) {
                    parts.push(`${failed} not applied.`);
                }
                if (parts.length > 0) {
                    const message = parts.join(' ');
                    // Nothing changed is information, not a success.
                    if (updated > 0) {
                        vm.feedback.addSuccess(message);
                    } else {
                        vm.feedback.addNotice(message);
                    }
                }
                load();
            },
            function (error) {
                vm.bulkErrorMessage = describeError(error, genericSaveErrorMessage);
            }
        ).finally(function () {
            $rootScope.loading = false;
        });
    }

    function bulkReset() {
        vm.bulkText = null;
        vm.bulkResult = null;
        vm.bulkErrorMessage = null;
    }

    // --- Confirmation ------------------------------------------------------------------------
    //
    // Every change here moves live device traffic between environments, so none of them happen on
    // a single click.

    function confirmRouting(imei, relayEnabled, action) {
        askToConfirm({
            title: relayEnabled ? 'Relay to EU' : 'Process in US',
            verb: relayEnabled ? 'relay' : 'process',
            subject: imei,
            suffix: relayEnabled ? ' to the EU' : ' in the US',
            detail: relayEnabled ? euDetail : null
        }, action);
    }

    function askToConfirm(confirmation, action) {
        vm.confirmation = confirmation;
        pendingAction = action;
        $(confirmModalSelector).modal('show');
    }

    // The wording is left in place so the dialog does not go blank while it fades out.
    function confirmSubmit() {
        const action = pendingAction;
        pendingAction = null;
        $(confirmModalSelector).modal('hide');
        if (action) {
            action();
        }
    }

    function confirmCancel() {
        pendingAction = null;
    }
}
